import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { DEFAULT_RATE_CARD, DealLineInput, PricedDeal, RateCard, money, priceDeal } from './rate-card';

export interface DealDraft {
  qty: Record<string, number>;
  termMonths: number;
  salesDiscountPercent: number;
}

export const EMPTY_DEAL: DealDraft = { qty: {}, termMonths: 12, salesDiscountPercent: 0 };

const LIMIT_ORDER = ['maxProducts', 'maxRoomAnalysesPerMonth', 'monthlyCredits', 'maxShopperChatsPerMonth', 'maxSeats'];

/**
 * Price an Enterprise deal from the rate card (docs/pricing-v2.md). Every
 * deal is the same unit prices at a different volume, so a $2k and a $10k
 * account can be explained line by line. Used on the deal-desk page and, with
 * `onUse`, to fill a vendor's contract.
 */
export function DealCalculator({
  initial,
  onUse,
  useLabel,
  compact,
}: {
  initial?: DealDraft;
  onUse?: (deal: PricedDeal, draft: DealDraft, card: RateCard) => void;
  useLabel?: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const plansApi = usePlansApi();
  const [card, setCard] = useState<RateCard>(DEFAULT_RATE_CARD);
  const [draft, setDraft] = useState<DealDraft>(initial ?? EMPTY_DEAL);

  useEffect(() => {
    Promise.resolve(plansApi.getRateCard())
      .then((c) => c && Array.isArray((c as RateCard).items) && setCard(c as RateCard))
      .catch(() => { /* the bundled card is the same until the API says otherwise */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lines: DealLineInput[] = useMemo(
    () => card.items.map((i) => ({ key: i.key, qty: i.base ? 1 : draft.qty[i.key] ?? 0 })),
    [card, draft.qty],
  );
  const deal = useMemo(
    () => priceDeal({ lines, termMonths: draft.termMonths, salesDiscountPercent: draft.salesDiscountPercent }, card),
    [lines, draft.termMonths, draft.salesDiscountPercent, card],
  );

  const setQty = (key: string, qty: number) =>
    setDraft((d) => ({ ...d, qty: { ...d.qty, [key]: Math.max(0, Math.min(99, qty)) } }));
  const itemLabel = (key: string, fallback: string) => t(`PRICING.RATE.${key}.LABEL`, { defaultValue: fallback });
  const itemUnit = (key: string, fallback: string) => t(`PRICING.RATE.${key}.UNIT`, { defaultValue: fallback });

  return (
    <div className={cn('grid grid-cols-1 gap-6', !compact && 'lg:grid-cols-[minmax(0,1fr)_340px]')} data-testid="deal-calculator">
      <div className="flex flex-col gap-5">
        <div className="divide-y rounded-xl border bg-card">
          {card.items.map((item) => {
            const qty = item.base ? 1 : draft.qty[item.key] ?? 0;
            return (
              <div key={item.key} className="flex items-center gap-4 px-4 py-3" data-testid={`deal-line-${item.key}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{itemLabel(item.key, item.label)}</p>
                  <p className="truncate text-xs text-muted-foreground">{itemUnit(item.key, item.unit)}</p>
                </div>
                <p className="w-28 shrink-0 text-end text-sm tabular-nums text-muted-foreground">
                  {money(item.unitPrice, card.currency)}
                  <span className="text-xs">{item.oneTime ? ` ${t('PRICING.DEAL.ONCE')}` : t('PRICING.UPGRADE.PER_MONTH')}</span>
                </p>
                {item.base ? (
                  <span className="w-[104px] shrink-0 text-center text-xs text-muted-foreground">{t('PRICING.DEAL.INCLUDED')}</span>
                ) : (
                  <div className="flex w-[104px] shrink-0 items-center justify-between rounded-lg border">
                    <Button variant="ghost" size="icon" className="size-8" aria-label="-" onClick={() => setQty(item.key, qty - 1)} disabled={qty <= 0}>
                      <Minus className="size-3.5" />
                    </Button>
                    <span className="text-sm tabular-nums" data-testid={`deal-qty-${item.key}`}>{qty}</span>
                    <Button variant="ghost" size="icon" className="size-8" aria-label="+" data-testid={`deal-plus-${item.key}`} onClick={() => setQty(item.key, qty + 1)}>
                      <Plus className="size-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-end gap-6">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">{t('PRICING.DEAL.TERM')}</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              value={String(draft.termMonths)}
              onValueChange={(v) => v && setDraft((d) => ({ ...d, termMonths: Number(v) }))}
              data-testid="deal-term"
            >
              {card.termDiscounts.map((td) => (
                <ToggleGroupItem key={td.termMonths} value={String(td.termMonths)} className="px-4" data-testid={`deal-term-${td.termMonths}`}>
                  {t('PRICING.DEAL.MONTHS', { count: td.termMonths })}
                  {td.percent > 0 && <span className="ms-1.5 text-xs text-muted-foreground">−{td.percent}%</span>}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="deal-discount" className="text-xs text-muted-foreground">{t('PRICING.DEAL.SALES_DISCOUNT')}</Label>
            <div className="flex items-center gap-2">
              <Input
                id="deal-discount"
                type="number"
                min={0}
                max={100}
                className="w-24 tabular-nums"
                value={draft.salesDiscountPercent}
                onChange={(e) => setDraft((d) => ({ ...d, salesDiscountPercent: parseFloat(e.target.value) || 0 }))}
                data-testid="deal-discount"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
        </div>
      </div>

      <div className={cn('flex flex-col gap-4 self-start rounded-xl border bg-card p-5', !compact && 'lg:sticky lg:top-4')} data-testid="deal-summary">
        <div>
          <p className="text-xs text-muted-foreground">{t('PRICING.DEAL.MONTHLY')}</p>
          <p className="font-heading mt-1 text-3xl font-medium tracking-tight tabular-nums" data-testid="deal-monthly">
            {money(deal.monthlyAmount, card.currency)}
            <span className="ms-1 text-sm font-normal text-muted-foreground">{t('PRICING.UPGRADE.PER_MONTH')}</span>
          </p>
        </div>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">{t('PRICING.DEAL.LIST')}</dt>
          <dd className="text-end tabular-nums">{money(deal.listMonthly, card.currency)}</dd>
          {deal.termDiscountPercent > 0 && (
            <>
              <dt className="text-muted-foreground">{t('PRICING.DEAL.TERM_DISCOUNT', { count: draft.termMonths })}</dt>
              <dd className="text-end tabular-nums">−{deal.termDiscountPercent}%</dd>
            </>
          )}
          {deal.salesDiscountPercent > 0 && (
            <>
              <dt className="text-muted-foreground">{t('PRICING.DEAL.SALES_DISCOUNT')}</dt>
              <dd className={cn('text-end tabular-nums', deal.discountWarning && 'text-destructive')}>−{deal.salesDiscountPercent}%</dd>
            </>
          )}
          <dt className="border-t pt-1.5 text-muted-foreground">{t('PRICING.DEAL.CONTRACT_VALUE', { count: Math.max(card.minTermMonths, draft.termMonths) })}</dt>
          <dd className="border-t pt-1.5 text-end font-medium tabular-nums" data-testid="deal-contract-value">{money(deal.contractValue, card.currency)}</dd>
          {deal.oneTimeTotal > 0 && (
            <>
              <dt className="text-muted-foreground">{t('PRICING.DEAL.ONE_TIME')}</dt>
              <dd className="text-end tabular-nums">{money(deal.oneTimeTotal, card.currency)}</dd>
            </>
          )}
        </dl>

        <div>
          <p className="mb-1.5 text-xs text-muted-foreground">{t('PRICING.DEAL.INCLUDES')}</p>
          <ul className="flex flex-col gap-1 text-sm">
            {LIMIT_ORDER.filter((k) => deal.limits[k] !== undefined).map((k) => (
              <li key={k} className="flex justify-between gap-3">
                <span className="text-muted-foreground">{t(`FREE_PLAN.LIMITS.${k}`)}</span>
                <span className="tabular-nums">{deal.limits[k].toLocaleString()}</span>
              </li>
            ))}
            {(draft.qty.website ?? 0) > 0 && (
              <li className="flex justify-between gap-3">
                <span className="text-muted-foreground">{t('PRICING.DEAL.WEBSITES')}</span>
                <span className="tabular-nums">{1 + (draft.qty.website ?? 0)}</span>
              </li>
            )}
          </ul>
        </div>

        {(deal.discountWarning || deal.belowFloor) && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 p-3 text-xs text-destructive" data-testid="deal-warning">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            <span>{deal.belowFloor ? t('PRICING.DEAL.BELOW_FLOOR') : t('PRICING.DEAL.DISCOUNT_WARNING', { max: card.maxSalesDiscountPercent })}</span>
          </div>
        )}

        {onUse && (
          <Button variant="outline" className="w-full" data-testid="deal-use" onClick={() => onUse(deal, draft, card)}>
            {useLabel || t('PRICING.DEAL.USE')}
          </Button>
        )}
      </div>
    </div>
  );
}
